import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ClipboardCheck, ArrowRight, Clock } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { axiosInstance } from "@/config/api";

const todayKey = () => new Date().toLocaleDateString("en-CA");

export const SurveyPendingPrompt = ({ userId }) => {
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [open, setOpen] = useState(false);
  const storeKey = `survey_popup_snooze_${userId}`;

  useEffect(() => {
    axiosInstance.get("/survey/kids2027/my-pending").then((r) => {
      if (!r.data?.pending?.length) return;
      setInfo(r.data);
      if (localStorage.getItem(storeKey) !== todayKey()) setOpen(true);
    }).catch(() => {});
  }, [storeKey]);

  if (!info) return null;
  const names = info.pending.map((c) => c.nickname || c.name).join(", ");
  const closeLabel = info.close_date
    ? new Date(`${info.close_date}T00:00:00`).toLocaleDateString("ms-MY", { day: "numeric", month: "long", year: "numeric" })
    : "";
  const daysText = info.days_left > 0 ? `${info.days_left} hari lagi` : "Hari ini hari terakhir";
  const Countdown = ({ big }) => info.close_date ? (
    <div className={`inline-flex items-center gap-2 rounded-full bg-amber-50 border border-amber-200 text-amber-700 font-bold ${big ? "px-4 py-2 text-sm mt-1" : "px-3 py-1 text-xs mt-2"}`}
      data-testid={big ? "survey-pending-popup-days" : "survey-pending-card-days"}>
      <Clock className="w-4 h-4" /> Borang ditutup: {daysText} ({closeLabel})
    </div>
  ) : null;
  const go = () => { localStorage.setItem(storeKey, todayKey()); navigate(info.survey_path); };
  const snooze = () => { localStorage.setItem(storeKey, todayKey()); setOpen(false); };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6 rounded-3xl bg-white border-2 border-pink-300 p-5 shadow-lg flex flex-col sm:flex-row sm:items-center gap-4"
        data-testid="survey-pending-card"
      >
        <motion.div animate={{ rotate: [0, -10, 10, 0] }} transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 2 }}
          className="w-12 h-12 rounded-2xl bg-gradient-to-br from-pink-400 to-purple-500 text-white flex items-center justify-center shrink-0">
          <ClipboardCheck className="w-6 h-6" />
        </motion.div>
        <div className="flex-1">
          <p className="font-black text-lg text-gray-800">{info.program_name}</p>
          <p className="text-sm text-gray-600 mt-0.5">Sila sahkan status pendaftaran <b>{names}</b> bagi sesi {info.survey_year}.</p>
          <Countdown />
        </div>
        <Button onClick={go} className="shrink-0 rounded-2xl bg-pink-500 hover:bg-pink-600 text-white font-black" data-testid="survey-pending-card-btn">
          Isi Sekarang <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </motion.div>

      <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : snooze())}>
        <DialogContent className="max-w-sm rounded-3xl text-center" data-testid="survey-pending-popup">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 260, damping: 14 }}
            className="mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-pink-400 to-purple-500 text-white flex items-center justify-center shadow-lg">
            <ClipboardCheck className="w-10 h-10" />
          </motion.div>
          <DialogTitle className="text-xl font-black text-purple-700 mt-2">{info.program_name}</DialogTitle>
          <DialogDescription className="text-gray-600">
            Adakah anda ingin meneruskan pendaftaran <b>{names}</b> di Destiny Junior bagi sesi {info.survey_year}? Sila isi borang pengesahan.
          </DialogDescription>
          <div><Countdown big /></div>
          <Button onClick={go} className="w-full h-12 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-bold mt-2" data-testid="survey-pending-popup-fill-btn">
            Isi Sekarang <ArrowRight className="w-5 h-5 ml-1" />
          </Button>
          <Button variant="ghost" onClick={snooze} className="w-full rounded-xl text-gray-500" data-testid="survey-pending-popup-later-btn">
            <Clock className="w-4 h-4 mr-1" /> Ingatkan Nanti
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};
